import { Resources, useResource } from "Translations/Resources";
import type { StepData } from "Components/Shared/Stepper/types";
import InfoIcon from "@mui/icons-material/Info";
import WebIcon from "@mui/icons-material/Web";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useNavigate } from "Navigation/useNavigate";
import { appUrl } from "Utils/UrlUtils";
import { createElement } from "react";

const RESOURCES = Resources.Admin.Campaigns.Form;

export const STEP_ORDER: ReadonlyArray<CampaignStep> = [
  "basic-info",
  "landing-page",
  "thank-you-page",
  "validity",
];

export type CampaignStep =
  | "basic-info"
  | "landing-page"
  | "thank-you-page"
  | "validity";

type Props = {
  activeStep: CampaignStep;
  isEditMode: boolean;
  onStepChange?: (step: CampaignStep) => void;
};

export const useCampaignStepper = ({
  activeStep,
  isEditMode,
  onStepChange,
}: Props) => {
  const { t } = useResource();
  const navigate = useNavigate();

  const stepIndex = (step: CampaignStep) => STEP_ORDER.indexOf(step);

  const isLastStep = stepIndex(activeStep) === STEP_ORDER.length - 1;
  const isFirstStep = stepIndex(activeStep) === 0;
  const currentFormId = `campaign-${activeStep}-form`;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const advanceStep = () => {
    scrollToTop();
    const nextStep = STEP_ORDER[stepIndex(activeStep) + 1];
    if (nextStep) {
      onStepChange?.(nextStep);
    } else {
      navigate(appUrl("admin-campaigns"));
    }
  };

  const goToPreviousStep = () => {
    scrollToTop();
    const prevStep = STEP_ORDER[stepIndex(activeStep) - 1];
    if (prevStep) {
      onStepChange?.(prevStep);
    }
  };

  const isStepCompleted = (step: CampaignStep) => {
    if (isEditMode) {
      return true;
    }
    return stepIndex(activeStep) > stepIndex(step);
  };

  const steps: ReadonlyArray<StepData<CampaignStep>> = [
    {
      id: "basic-info",
      label: t(RESOURCES.StepBasicInfo),
      isCompleted: isStepCompleted("basic-info"),
      icon: createElement(InfoIcon),
    },
    {
      id: "landing-page",
      label: t(RESOURCES.StepLandingPage),
      isCompleted: isStepCompleted("landing-page"),
      icon: createElement(WebIcon),
    },
    {
      id: "thank-you-page",
      label: t(RESOURCES.StepThankYouPage),
      isCompleted: isStepCompleted("thank-you-page"),
      icon: createElement(FavoriteIcon),
    },
    {
      id: "validity",
      label: t(RESOURCES.StepValidity),
      isCompleted: false,
      icon: createElement(CalendarTodayIcon),
    },
  ];

  const activeStepIndex = steps.findIndex((s) => s.id === activeStep);

  const handleStepClick = (index: number) => {
    const step = steps[index];
    if (step) {
      scrollToTop();
      onStepChange?.(step.id);
    }
  };

  return {
    steps,
    activeStepIndex,
    handleStepClick,
    isFirstStep,
    isLastStep,
    currentFormId,
    advanceStep,
    goToPreviousStep,
  };
};
